import React, { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
	Background,
	BaseEdge,
	Controls,
	Edge,
	type EdgeProps,
	MarkerType,
	MiniMap,
	Node,
	ReactFlow,
	ReactFlowProvider,
	Handle,
	Position,
	useNodesState,
	useEdgesState,
	ConnectionLineType
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { PageConnectionEdge, PageConnectionNode, PageConnectionsViewModel } from "../API/Models";
import { LoadingSpinner } from "./LoadingSpinner";

type GraphProps = {
	data?: ExtendedConnections | null;
	loading?: boolean;
	error?: string | null;
	className?: string;
};

type NodeVariant = "main" | "incoming" | "outgoing" | "external" | "missing" | "layout";

type ExtendedConnections = PageConnectionsViewModel & { layout?: PageConnectionEdge[] | null };
type ExtendedNode = PageConnectionNode & { kind?: string | null };

const COLUMN_GAP = 260;
const ROW_GAP = 130;

const nodeStyle = (variant: NodeVariant): React.CSSProperties => {
	const base: React.CSSProperties = {
		padding: "12px 14px",
		borderRadius: 14,
		boxShadow: "0 6px 16px rgba(15,23,42,0.12)",
		background: "#fff",
		border: "1px solid #e2e8f0",
		minWidth: 180,
		maxWidth: 220,
		fontSize: 12,
		lineHeight: 1.35
	};

	if (variant === "main") {
		return { ...base, border: "1px solid #0f172a", background: "#0f172a", color: "#fff" };
	}
	if (variant === "missing") {
		return { ...base, border: "1px dashed #e11d48", background: "#fff1f2", color: "#be123c" };
	}
	if (variant === "external") {
		return { ...base, border: "1px solid #0ea5e9", background: "#ecfeff", color: "#0f172a" };
	}
	if (variant === "layout") {
		return { ...base, border: "1px solid #6366f1", background: "#eef2ff", color: "#0f172a" };
	}

	return base;
};

const ConnectionNode: React.FC<{ data: { title: string; subtitle?: string; variant: NodeVariant } }> = ({ data }) => (
	<div style={nodeStyle(data.variant)}>
		<Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
		<Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
		<div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, wordBreak: "break-word" }}>{data.title}</div>
		{data.subtitle && <div style={{ color: "rgba(15,23,42,0.65)", wordBreak: "break-word" }}>{data.subtitle}</div>}
	</div>
);

const nodeTypes = { connection: ConnectionNode };
export const connectionNodeTypes = nodeTypes;
export { ConnectionNode };

const formatNodeLabel = (node?: PageConnectionNode | null) =>
	node?.title || node?.slug || node?.id || "Untitled page";

const formatNodeSubtitle = (node?: PageConnectionNode | null) => {
	const slug = node?.slug ? `/${node.slug}` : "";
	const lang = node?.language ? node.language.toUpperCase() : "";
	if (slug && lang) return `${slug} • ${lang}`;
	return slug || lang || "";
};

const buildColumnPositions = (count: number): number[] => {
	if (count === 0) return [];
	if (count === 1) return [0];
	const start = -((count - 1) * ROW_GAP) / 2;
	return new Array(count).fill(0).map((_, idx) => start + idx * ROW_GAP);
};

const toNodeId = (prefix: string, ref?: PageConnectionNode | null, fallback?: string) =>
	`${prefix}-${ref?.id || ref?.slug || fallback || Math.random().toString(36).slice(2)}`;

const arrow = (color: string) => ({ type: MarkerType.ArrowClosed, width: 12, height: 12, color });

const EDGE_GAP = 14;
const ShortStraightEdge: React.FC<EdgeProps> = (props) => {
	const {
		sourceX,
		sourceY,
		targetX,
		targetY,
		markerEnd,
		markerStart,
		style,
		interactionWidth,
		data,
		source,
		target,
		id,
		selected
	} = props;

	const dx = targetX - sourceX;
	const dy = targetY - sourceY;
	const len = Math.sqrt(dx * dx + dy * dy) || 1;
	const gapX = (EDGE_GAP * dx) / len;
	const gapY = (EDGE_GAP * dy) / len;

	const sx = sourceX + gapX;
	const sy = sourceY + gapY;
	const tx = targetX - gapX;
	const ty = targetY - gapY;

	const path = `M ${sx},${sy} L ${tx},${ty}`;

	return <BaseEdge path={path} markerEnd={markerEnd} markerStart={markerStart} style={style} interactionWidth={interactionWidth} />;
};

const getVariantFromKind = (node: ExtendedNode | null | undefined, fallback: NodeVariant): NodeVariant => {
	const inferredKind =
		node?.kind ??
		(node?.slug === "layout" ? "layout" : undefined) ??
		(node?.title?.toLowerCase().includes("layout") ? "layout" : undefined);
	const kind = inferredKind?.toLowerCase();
	if (kind === "layout") return "layout";
	if (kind === "external") return "external";
	if (kind === "missing") return "missing";
	return fallback;
};

const buildGraph = (raw?: PageConnectionsViewModel | null) => {
	const data = raw as ExtendedConnections | null;
	if (!data) {
		return { nodes: [] as Node[], edges: [] as Edge[] };
	}

	const nodes = new Map<string, Node>();
	const edges: Edge[] = [];

	const addNode = (node: Node) => {
		if (!nodes.has(node.id)) {
			nodes.set(node.id, node);
		}
	};

	const addEdge = (edge: Edge) => {
		edges.push(edge);
	};

	const centerId = toNodeId("page", data.page, "center");
	addNode({
		id: centerId,
		type: "connection",
		position: { x: 0, y: 0 },
		data: { title: formatNodeLabel(data.page), subtitle: formatNodeSubtitle(data.page), variant: "main" }
	});

	const layout = data.layout ?? [];
	const external = data.external ?? [];

	const incoming = data.incoming ?? [];
	const outgoing = data.outgoing ?? [];

	const neighborKey = (node?: PageConnectionNode | null) =>
		`${node?.id ?? ""}|${node?.slug ?? ""}|${node?.language ?? ""}`;

	type NeighborBucket = { node: PageConnectionNode | null; incoming: PageConnectionEdge[]; outgoing: PageConnectionEdge[] };
	const buckets = new Map<string, NeighborBucket>();

	const ensureBucket = (node: PageConnectionNode | null) => {
		const key = neighborKey(node);
		if (!buckets.has(key)) {
			buckets.set(key, { node, incoming: [], outgoing: [] });
		}
		return buckets.get(key)!;
	};

	incoming.forEach((edge) => {
		const bucket = ensureBucket(edge.from);
		bucket.node = bucket.node ?? edge.from;
		bucket.incoming.push(edge);
	});

	outgoing.forEach((edge) => {
		const bucket = ensureBucket(edge.to);
		bucket.node = bucket.node ?? edge.to;
		bucket.outgoing.push(edge);
	});

	const onlyIncoming = Array.from(buckets.values()).filter((b) => b.incoming.length > 0 && b.outgoing.length === 0);
	const bidirectional = Array.from(buckets.values()).filter((b) => b.incoming.length > 0 && b.outgoing.length > 0);
	const onlyOutgoing = Array.from(buckets.values()).filter((b) => b.incoming.length === 0 && b.outgoing.length > 0);

	const layoutY = buildColumnPositions(layout.length);
	layout.forEach((edge, index) => {
		const nodeId = toNodeId("layout", edge.from, index.toString());
		addNode({
			id: nodeId,
			type: "connection",
			position: { x: -COLUMN_GAP * 2, y: layoutY[index] },
			data: {
				title: formatNodeLabel(edge.from),
				subtitle: formatNodeSubtitle(edge.from),
				variant: "layout"
			}
		});

		addEdge({
			id: `e-layout-${nodeId}-${centerId}-${index}`,
			source: nodeId,
			target: centerId,
			type: "short",
			markerEnd: arrow("#6366f1"),
			style: { stroke: "#6366f1", strokeWidth: 2 }
		});
	});

	const incomingY = buildColumnPositions(onlyIncoming.length);
	onlyIncoming.forEach((bucket, index) => {
		const variant = getVariantFromKind(bucket.node as ExtendedNode, "incoming");
		const nodeId = toNodeId("in", bucket.node, index.toString());
		addNode({
			id: nodeId,
			type: "connection",
			position: { x: -COLUMN_GAP, y: incomingY[index] },
			data: {
				title: formatNodeLabel(bucket.node),
				subtitle: formatNodeSubtitle(bucket.node),
				variant
			}
		});

		bucket.incoming.forEach((edge, edgeIdx) => {
			const stroke = "#0f172a";
			addEdge({
				id: `e-in-${nodeId}-${centerId}-${index}-${edgeIdx}`,
				source: nodeId,
				target: centerId,
				type: "short",
				markerEnd: arrow(stroke),
				style: { stroke, strokeWidth: 2 }
			});
		});
	});

	const outgoingY = buildColumnPositions(onlyOutgoing.length + bidirectional.length);

	const addOutgoingNodeAndEdges = (bucket: NeighborBucket, index: number, bidirectionalEdge: boolean) => {
		const hasMissingOutgoing = bucket.outgoing.length > 0 && bucket.outgoing.every((e) => !e.exists);
		const fallbackVariant: NodeVariant = hasMissingOutgoing ? "missing" : "outgoing";
		const variant = getVariantFromKind(bucket.node as ExtendedNode, fallbackVariant);
		const nodeId = toNodeId("out", bucket.node, index.toString());
		addNode({
			id: nodeId,
			type: "connection",
			position: { x: COLUMN_GAP, y: outgoingY[index] },
			data: {
				title: formatNodeLabel(bucket.node),
				subtitle: formatNodeSubtitle(bucket.node),
				variant
			}
		});

		if (bidirectionalEdge)
		{
			const anyExists = bucket.outgoing.some((e) => e.exists);
			const stroke = anyExists ? "#0f172a" : "#e11d48";
			addEdge({
				id: `e-bi-${centerId}-${nodeId}-${index}`,
				source: centerId,
				target: nodeId,
				type: "short",
				markerStart: arrow(stroke),
				markerEnd: arrow(stroke),
				style: {
					stroke,
					strokeWidth: 2,
					strokeDasharray: anyExists ? undefined : "6 4"
				}
			});
			return;
		}

		bucket.outgoing.forEach((edge, edgeIdx) => {
			const stroke = edge.exists ? "#0f172a" : "#e11d48";
			addEdge({
				id: `e-out-${centerId}-${nodeId}-${index}-${edgeIdx}`,
				source: centerId,
				target: nodeId,
				type: "short",
				markerEnd: arrow(stroke),
				style: {
					stroke,
					strokeWidth: 2,
					strokeDasharray: edge.exists ? undefined : "6 4"
				}
			});
		});
	};

	let outIndex = 0;
	bidirectional.forEach((bucket) => {
		addOutgoingNodeAndEdges(bucket, outIndex, true);
		outIndex += 1;
	});

	onlyOutgoing.forEach((bucket) => {
		addOutgoingNodeAndEdges(bucket, outIndex, false);
		outIndex += 1;
	});

	const externalY = buildColumnPositions(external.length);
	external.forEach((link, index) => {
		const nodeId = `ext-${index}`;
		addNode({
			id: nodeId,
			type: "connection",
			position: { x: COLUMN_GAP * 2, y: externalY[index] },
			data: {
				title: link.url || "External link",
				subtitle: link.label || link.path || "",
				variant: "external"
			}
		});

		addEdge({
			id: `e-ext-${centerId}-${nodeId}-${index}`,
			source: centerId,
			target: nodeId,
			markerEnd: arrow("#0ea5e9"),
			style: { stroke: "#0ea5e9", strokeWidth: 2 }
		});
	});

	return { nodes: Array.from(nodes.values()), edges };
};

/** Interactive page connections graph (incoming, outgoing, external, layout) using ReactFlow. */
export const PageConnectionsGraph: React.FC<GraphProps> = ({ data, loading, error, className }) => {
	const { nodes, edges } = useMemo(() => buildGraph(data), [data]);
	const [rfNodes, setRfNodes, onNodesChange] = useNodesState(nodes);
	const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState(edges);

	useEffect(() => {
		setRfNodes(nodes);
		setRfEdges(edges);
	}, [nodes, edges, setRfNodes, setRfEdges]);

	useEffect(() => {
		if (error) {
			toast.error(error);
		}
	}, [error]);

	const heightClass = className ?? "h-[520px]";
	const containerBase = "rounded-2xl border border-slate-200 bg-white shadow-sm";
	const container = `${containerBase} ${heightClass}`;

	if (loading) {
		return (
			<div className={`flex items-center justify-center ${container}`}>
				<LoadingSpinner size="lg" />
			</div>
		);
	}

	if (error) {
		return (
			<div className={`rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 shadow-sm ${heightClass}`}>
				{error}
			</div>
		);
	}

	if (!data || nodes.length === 0) {
		return (
			<div
				className={`flex items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500 ${heightClass}`}
			>
				No connection data yet.
			</div>
		);
	}

	return (
		<div className={container}>
			<ReactFlowProvider>
				<ReactFlow
					nodes={rfNodes}
					edges={rfEdges}
					onNodesChange={onNodesChange}
					onEdgesChange={onEdgesChange}
					nodeTypes={nodeTypes}
					fitView
					fitViewOptions={{ padding: 0.4 }}
					minZoom={0.3}
					maxZoom={1.2}
					panOnScroll
					zoomOnScroll
					attributionPosition="bottom-right"
					defaultEdgeOptions={{ type: "short" }}
					connectionLineType={ConnectionLineType.Straight}
					edgeTypes={{ short: ShortStraightEdge }}
				>
					<Background gap={20} size={1} color="#e2e8f0" />
					<MiniMap pannable zoomable />
					<Controls showInteractive={false} />
				</ReactFlow>
			</ReactFlowProvider>
		</div>
	);
};

export default PageConnectionsGraph;

